<template>
  <div class="w-full grid lg:grid-cols-2 col-gap-6 row-gap-8 items-center">
    <div class="col-span-2 text-xl font-bold text-text leading-tight mb-2">{{ $t('title') }}</div>
    <BaseInput class="col-start-1" v-model="space_title"
               :disabled="currentUserIsWatcher"
               @focusout="changeName"
               :label="$t('spaceName')"/>
    <div class="text-text-ternary text-sm col-start-1 lg:col-start-2">
      ID:
      <span class="text-text-label border-b border-dotted hover:text-red-hover transition duration-200 cursor-pointer"
            v-tooltip="$t('clickToCopy')"
            @click="copySpaceId">
        {{ current_space.data.id }}
      </span>
    </div>
    <BaseSelect class="col-start-1" :value="current_space.data.currency"
                :options="currenciesList"
                :select-label="$t('currencyLabel')"
                :label="`name_${$i18n.locale}`"
                :searchable="true"
                :disabled="true"
                :show-labels="false"/>
    <SpaceTypePicker class="col-start-1" :selected="space_type"
                     :disabled="currentUserIsWatcher"
                     :membersAmount="current_space_members.length"
                     @change="changeType($event)"/>
    <div class="col-start-1">
      <div class="mb-3-5">
        <div class="text-text-label text-xs">{{ $t('setUseTimeLabel') }}</div>
      </div>

      <BaseRadioGroup :value="current_space.data.time_enabled"
                      :options="yesNoOptions"
                      :disabled="currentUserIsWatcher"
                      @change="$emit('settingsChanged', {time_enabled:$event})"/>
    </div>
    <div class="col-span-2">
      <div class="mb-3-5">
        <div class="text-text-label text-xs">{{ $t('setUseAuthorLabel') }}</div>
        <div class="text-xs text-text-ternary mt-2">{{ $t('setUseAuthorTip') }}</div>
      </div>

      <BaseRadioGroup :value="current_space.data.author_enabled"
                      :options="yesNoOptions"
                      :disabled="currentUserIsWatcher"
                      @change="$emit('settingsChanged', {author_enabled:$event})"/>
    </div>
    <div class="col-span-2">
      <div class="mb-3-5">
        <div class="text-text-label text-xs">{{ $t('setUseContractorsLabel') }}</div>
        <div class="text-xs text-text-ternary mt-2">{{ $t('setUseContractorsTip') }}</div>
      </div>

      <BaseRadioGroup :value="current_space.data.contractors_enabled"
                      :options="yesNoOptions"
                      :disabled="currentUserIsWatcher"
                      @change="$emit('settingsChanged', {contractors_enabled:$event})"/>
    </div>
  </div>
</template>

<script>
import {mapGetters, mapState} from "vuex";
import BaseSelect from "@/components/UI/BaseSelect/BaseSelect";
import BaseRadioGroup from "@/components/UI/BaseRadioGroup";
import BaseInput from "@/components/UI/BaseInput/BaseInput";
import SpaceTypePicker from "@/components/common/SpaceTypePicker/SpaceTypePicker";
import copyTextToClipboard from "@/utilities/copyTextToClipboard";

export default {
  name: "OptionsGeneral",
  components: {SpaceTypePicker, BaseInput, BaseRadioGroup, BaseSelect},
  data() {
    return {
      space_title: '',
      space_type: 1,
    }
  },
  computed: {
    ...mapState('space', ['current_space']),
    ...mapState('space/access', ['current_space_members']),
    ...mapGetters('space', ['currentUserIsWatcher']),
    ...mapGetters('cabinet/currencies', ['currenciesList']),
    yesNoOptions() {
      return [
        {value: true, label: this.$t('yes'), disabled: this.currentUserIsWatcher,},
        {value: false, label: this.$t('no'), disabled: this.currentUserIsWatcher,}
      ]
    }
  },
  methods: {
    changeName() {
      if (this.space_title === this.current_space.data.title) return
      if (this.space_title === '') {
        this.space_title = this.current_space.data.title
      } else {
        this.$emit('settingsChanged', {title: this.space_title})
      }
    },
    changeType(val) {
      // prevent if user want to change space_type to private and it has more that 1 member
      if (val === 1 && this.space_type === 2 && this.current_space_members.length > 1) {
        this.$toasted.error('Вы не можете выбрать тип «Личная», так как в пространстве больше одного участника');
        return
      }
      this.space_type = val
      this.$emit('settingsChanged', {type: val})
    },
    async copySpaceId() {
      try {
        const copied = await copyTextToClipboard(this.current_space.data.id)
        if (copied) {
          this.$toasted.success(this.$t('copySuccess'))
        }
      } catch (err) {
        console.log(err)
      }
    }
  },
  created() {
    this.space_title = this.current_space.data.title
    this.space_type = this.current_space.data.type
  },
  i18n: {
    messages: {
      en: {
        clickToCopy: 'Click to copy',
        copySuccess: 'Copied successfully',
        title: 'Settings',
        spaceName: 'Workspace name',
        currencyLabel: 'Base currency',
        setUseTimeLabel: 'Use time tracking in operations?',
        setUseAuthorLabel: 'Indicate who made the operation?',
        setUseAuthorTip: 'The details will appear in the operation editing window.',
        setUseContractorsLabel: 'Indicate counterparties in operations?',
        setUseContractorsTip: 'You can specify who pays you and who you pay. This may be important if the payments are regular.',
        yes: 'Yes',
        no: 'No'
      },
      ru: {
        clickToCopy: 'Нажмите, чтобы скопировать',
        copySuccess: 'Успешно скопировано',
        title: 'Настройки',
        spaceName: 'Название пространства',
        currencyLabel: 'Базовая валюта',
        setUseTimeLabel: 'Использовать учет времени в операциях?',
        setUseAuthorLabel: 'Указывать, кто внес операцию?',
        setUseAuthorTip: 'Сведения появятся в окне редактирования операции.',
        setUseContractorsLabel: 'Указывать контрагентов в операциях?',
        setUseContractorsTip: 'Вы можете указать, кто платит вам и кому платите вы. Это может быть важно, если платежи регулярные.',
        yes: 'Да',
        no: 'Нет'
      }
    }
  }
}
</script>
